import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardBody, Box, Text, Button, Menu, Header } from 'grommet'
import { Close, MoreVertical } from 'grommet-icons'
import {
  ActionCreators,
  DateTimeUtils,
  CalendarUtilsWithExtras,
  Selectors,
  TaskManager,
  AlarmUtils
} from 'galarm-shared'
import { I18n, GlobalConfig, Constants } from 'galarm-config'
import LabelledText from './LabelledText'
import AppCanvas from './AppCanvas'
import { SecondaryText } from 'web-components'
import moment from 'moment-timezone'
import { NavigationUtils } from 'galarm-ps-api'

const { makeCalendarEventSelector, makeConnectedAccountSelector } = Selectors

const CalendarEventDetailsHeader = ({ onClose, menuOptions }) => (
  <Header className="header" background="brand" pad="small">
    <Text color="white">{I18n.t('calendarEvent')}</Text>
    <Box direction="row">
      <Button icon={<Close />} onClick={onClose} />
      <Menu
        id="calendar-event-details-menu"
        dropBackground="lightTint"
        icon={<MoreVertical />}
        items={menuOptions}
      />
    </Box>
  </Header>
)

const CalendarEventDetails = ({ eventId }) => {
  const dispatch = useDispatch()
  const calendarEventSelector = makeCalendarEventSelector()
  const calendarEvent = useSelector(state =>
    calendarEventSelector(state, { eventId })
  )
  const connectedAccountSelector = makeConnectedAccountSelector()
  const connectedAccount = useSelector(state =>
    connectedAccountSelector(state, {
      accountId: calendarEvent?.connectedAccountId
    })
  )

  const {
    summary,
    start,
    end,
    recurrence,
    calendarName,
    lastSnoozeOn,
    lastSnoozeFor,
    hangoutLink,
    attendees,
    organizer,
    description
  } = calendarEvent

  const eventInSnooze =
    CalendarUtilsWithExtras.isLastSnoozeTimeInSnoozeDuration(
      lastSnoozeOn,
      lastSnoozeFor
    )

  const timezone = start?.timeZone || 'UTC'
  const eventDate = AlarmUtils.getCalendarEventDate(start, timezone)

  const nextEventDate =
    !recurrence || recurrence.length === 0
      ? eventDate
      : eventInSnooze
      ? DateTimeUtils.getPrevOccurrenceForCalendarEvent(calendarEvent)
      : DateTimeUtils.getNextOccurrenceForCalendarEvent(calendarEvent)

  // Validate nextEventDate before using it
  const validNextEventDate =
    nextEventDate && moment(nextEventDate).isValid()
      ? nextEventDate
      : Date.now()

  const timeString = DateTimeUtils.getLocalizedTimeAsString(
    moment(validNextEventDate).valueOf()
  )
  const dateString =
    DateTimeUtils.getLocalizedDayDateWithYearForLaterDatesAsString(
      moment(validNextEventDate).valueOf()
    )
  const repeatString =
    DateTimeUtils.getRepetitionStringForCalendarEvent(calendarEvent)

  const accountEmail = connectedAccount?.user?.email
  const calendarNameString = `${calendarName} - ${accountEmail}`

  // Calculate duration between start and end
  const calculateDuration = () => {
    if (!end?.dateTime || !start?.dateTime) {
      return null
    }

    const startDateTime = moment.tz(start.dateTime, timezone)
    const endDateTime = moment.tz(end.dateTime, timezone)

    if (!startDateTime.isValid() || !endDateTime.isValid()) {
      return null
    }

    const duration = moment.duration(endDateTime.diff(startDateTime))

    if (duration.asMilliseconds() <= 0) {
      return null
    }

    return duration.asMilliseconds()
  }

  const formatDuration = duration => {
    if (!duration) {
      return I18n.t('notSpecified') || 'Not specified'
    }

    return DateTimeUtils.getDurationAsString(duration)
  }

  const duration = calculateDuration()
  const durationString = formatDuration(duration)

  const handleShowInCalendar = () => {
    if (calendarEvent.htmlLink) {
      window.open(calendarEvent.htmlLink, '_blank')
    }
  }

  const handleIgnoreEvent = () => {
    dispatch(
      ActionCreators.setProgress(
        Constants.ProgressStates.IN_PROGRESS,
        I18n.t('ignoreEvent')
      )
    )
    TaskManager.addHttpsCloudTask('ignoreCalendarEvents', {
      uid: GlobalConfig.uid,
      events: [
        {
          id: calendarEvent.id,
          connectedAccountId: calendarEvent.connectedAccountId,
          calendarId: calendarEvent.calendarId
        }
      ]
    })
      .then(() => {
        NavigationUtils.dismissProgress()
      })
      .catch(error => {
        console.log('Error ignoring calendar event:', error)
        NavigationUtils.dismissProgress()
      })
  }

  const handleUnignoreEvent = () => {
    dispatch(
      ActionCreators.setProgress(
        Constants.ProgressStates.IN_PROGRESS,
        I18n.t('unignoreEvent')
      )
    )
    TaskManager.addHttpsCloudTask('unignoreCalendarEvents', {
      uid: GlobalConfig.uid,
      events: [
        {
          id: calendarEvent.id,
          connectedAccountId: calendarEvent.connectedAccountId,
          calendarId: calendarEvent.calendarId
        }
      ]
    })
      .then(() => {
        NavigationUtils.dismissProgress()
      })
      .catch(error => {
        console.log('Error unignoring calendar event:', error)
      })
  }

  const menuOptions = [
    {
      label: I18n.t('showInCalendar'),
      onClick: handleShowInCalendar
    },
    calendarEvent.ignored
      ? {
          label: I18n.t('unignoreEvent'),
          onClick: handleUnignoreEvent
        }
      : {
          label: I18n.t('ignoreEvent'),
          onClick: handleIgnoreEvent
        }
  ]

  const closeCalendarEventDetailsScreen = () => {
    dispatch(ActionCreators.hideCalendarEventDetailsScreen())
  }

  return (
    <AppCanvas>
      <CalendarEventDetailsHeader
        onClose={closeCalendarEventDetailsScreen}
        menuOptions={menuOptions}
      />
      <Card
        margin="small"
        background="textBackgroundColor"
        width="600px"
        flex={{ shrink: 0 }}>
        <CardBody gap="medium" pad="medium">
          <LabelledText
            label={I18n.t('title')}
            text={summary}
            border="bottom"
          />
          <Box direction="row" gap="medium">
            <LabelledText
              label={I18n.t('date')}
              text={dateString}
              border="bottom"
            />
            <LabelledText
              label={I18n.t('time')}
              text={timeString}
              border="bottom"
            />
          </Box>
          <LabelledText
            label={I18n.t('duration')}
            text={durationString}
            border="bottom"
          />
          <LabelledText
            label={I18n.t('repeat')}
            text={repeatString}
            border="bottom"
          />
          <LabelledText
            label={I18n.t('calendarName')}
            text={calendarNameString}
            border="bottom"
          />
          {hangoutLink && (
            <LabelledText
              label={I18n.t('meetingLink')}
              text={hangoutLink}
              onClick={() => window.open(hangoutLink, '_blank')}
              border="bottom"
            />
          )}
          {attendees && (
            <LabelledText
              label={I18n.t('attendees')}
              text={attendees.map(a => a.email).join(', ')}
              border="bottom"
            />
          )}
          {organizer?.displayName && (
            <LabelledText
              label={I18n.t('organizer')}
              text={organizer.displayName}
              border="bottom"
            />
          )}
          {description && (
            <Box gap="small">
              <SecondaryText color="darkTint">{I18n.t('notes')}</SecondaryText>
              <Box direction="row" border="bottom">
                <Text style={{ wordBreak: 'break-all' }}>{description}</Text>
              </Box>
            </Box>
          )}
        </CardBody>
        {/* CardFooter and snooze button removed as per request */}
      </Card>
    </AppCanvas>
  )
}

export default CalendarEventDetails
